import React, { useEffect, useState } from 'react';
import { compose } from 'react-apollo';
import { change } from 'redux-form';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import get from 'lodash/get';
import usePrevious from 'chameleon/ui-stack/hooks/use-previous-props';
import { getMinTimezone } from 'chameleon-src/utility/utils';
import {
  checkIfNoSecureQuestions,
  shouldTurnOnSecureAssessment,
} from 'chameleon-src/utility/helpers';
import {
  findQBSubject,
  getFinishedPercent,
} from 'chameleon-src/components/learnosity/utils/helpers';
import {
  ATTESTATION_PREFERENCE,
  ATTESTATION_SUBJECT,
  QuestionTypes,
} from 'chameleon-src/constants/types';
import { ASSIGN_MANAGE_FORM_NAME, PartType } from '../../utils/constants';
import { getFormIdField } from '../../utils';
import getSettings from './utils/getSettings';
import getInitialState from './utils/getInitialState';
import getAssignments from './utils/getAssignments';
import { withAssessmentGroupInfo } from '../utils/gql';

const AssessmentWrapper = (WrappedComponent) => {
  const InnerComponent = (props) => {
    const {
      globals,
      isAssign,
      assessmentGroupInfo,
      isFinalExam,
      assignment,
      params: { subject },
      userData,
      itemId,
      individualStudentAssignmentFlag,
      subject: { id: masterSubjectId, classes: subjectClasses },
      assessmentWithQuestionsData,
      loadingGroupedAssignments,
      assessments,
      shouldFetchOtherAssignments = false,
      fetchOtherAssignments,
      showAttestationFlag,
      hasPracticeExamBehavior,
      teacherSubjects,
      isPreApSubject,
      hasAudioRecordOrFileUpload,
      formData,
      isPracticeExam,
      hasSecureQns,
      me,
      savePreference,
      assignmentStudentsStatusData,
      initialized: wasFormInitialized,
      sectionId,
      assignmentId,
      dispatch,
      showStudentScoring,
      isStudentsFormChanged,
      handleShowClassSwitchingInfoModal,
      router,
      setSelectedAssignmentUrl,
      manageMultipleClassesFlag,
      canUpdateAll,
      assignmentsResponse,
      onAssign,
      allSections,
      openAssignments,
      isPPC,
      isSPP,
      hasPreassignedBehavior,
      chosenEducationPeriod,
      params,
    } = props;
    const {
      isLoading: isGlobalLoading,
      state: globalState,
      setState: setGlobalState,
      classesStats: globalClassesStats,
      dispatchClassesStats: dispatchGlobalClassesStats,
      autoSelectedGroups: globalAutoSelectedGroups,
      selectedGroups: globalSelectedGroups,
      disabledStudents: globalDisabledStudents,
      setDisabledStudents: setGlobalDisabledStudents,
      submit: globalSubmit,
      initForm,
      hasNoSubStudents,
      storedFormValues: globalStoredFormValues,
      updateForm: updateGlobalForm,
      defaultTimezone,
      hasUpdatedOptions,
      setCanSubmit: setGlobalCanSubmit,
    } = globals;
    const {
      printToggle,
      attestationAgreementChecked,
      isSecure,
      hasScrambledOrder,
      hasForcedScrambledOrder,
      title,
      studentScoring,
      students: formStudents,
      startsAt,
      dueAt,
      timer,
      allowLateSubmission,
      allAssignees,
      testTakingLocation,
      scoreDisplay,
      startsAtPart2,
      partType,
      isSoftTimer,
    } = formData;
    const [state, setState] = useState(getInitialState(props));
    const [selectedAssignment, setSelectedAssignment] = useState(
      assignment || {},
    );
    const {
      isLoading,
      gettingAssignmentGroups,
      assignmentGroupsLoaded,
      sectionsMinTimezone,
      isNotPreAPSubjectAndNotPPC,
      isFullyMCQ,
      ldbEnabled,
    } = state;
    const prevProps = usePrevious(props);
    const isMCQPPC =
      isPPC &&
      get(assessmentWithQuestionsData, 'questions[0].type', '') ===
        QuestionTypes.MULTIPLE_CHOICE_QUESTION;
    const handleShouldApplyRestriction = () =>
      showAttestationFlag &&
      isNotPreAPSubjectAndNotPPC &&
      checkIfNoSecureQuestions(
        assessmentWithQuestionsData,
        hasPracticeExamBehavior,
      );
    // we use this object to compare the keys to the rest of the assignees
    // to see if we should enable the "Update All" button
    const comparisonAssignment = getSettings(selectedAssignment);

    let isUpdateAllEnabled = false;
    if (!isAssign && openAssignments.length > 1) {
      openAssignments.forEach((assignee) => {
        const assigneeSettings = getSettings(assignee);
        const hasSameSettings = _.isEqual(
          assigneeSettings,
          comparisonAssignment,
        );
        const hasSameId = Number(assignee.id) === Number(selectedAssignment.id);
        if (!hasSameId && !isUpdateAllEnabled) {
          isUpdateAllEnabled = !hasSameSettings;
        }
      });
    }

    useEffect(() => {
      if (isAssign && !(!assessmentGroupInfo && isFinalExam)) {
        (async () =>
          await getAssignments(
            {
              state,
              gettingAssignmentGroups,
              assignmentGroupsLoaded,
              assessmentGroupInfo,
              classesStats: globalClassesStats,
              isFinalExam,
              assignment,
              subject,
              userData,
              itemId,
              individualStudentAssignmentFlag,
              subjectClasses,
              autoSelectedGroups: globalAutoSelectedGroups,
              selectedGroups: globalSelectedGroups,
              assessmentWithQuestionsData,
              hasPreassignedBehavior,
            },
            (newState, newDisabledStudents, newStats) => {
              const globalStateKeys = Object.keys(globalState);
              setGlobalState({
                ...globalState,
                ...pick(newState, globalStateKeys),
              });
              setGlobalDisabledStudents(newDisabledStudents);
              dispatchGlobalClassesStats(newStats);
              setState(omit(newState, globalStateKeys));
            },
          ))();
      } else {
        setTimeout(() => {
          setState({
            ...state,
            isLoading: false,
          });
        }, 0);
      }
    }, []);

    useEffect(() => {
      // Do this only if the form was already initialized. Otherwise this is done in `initForm` method.
      if (!wasFormInitialized) return;

      // Update `disabledStudents` with new data from `assignmentStudentsStatusData`
      const currentAssignmentStudents =
        assignmentStudentsStatusData[assignmentId] || {};
      const currentAssignmentStudentIds = Object.keys(
        currentAssignmentStudents,
      );
      const currentAssignmentDisabledStudentIds =
        currentAssignmentStudentIds.filter(
          (studentId) => !!currentAssignmentStudents[studentId],
        );
      // Filter disabled students from other assignments or from other associated ISAs.
      const disabledStudentsFromOtherAssignments =
        globalDisabledStudents.filter(
          (disabledStudent) =>
            disabledStudent.assignmentId !== assignmentId ||
            !currentAssignmentDisabledStudentIds.includes(
              disabledStudent.studentId,
            ),
        );

      setGlobalDisabledStudents(
        disabledStudentsFromOtherAssignments.concat(
          currentAssignmentDisabledStudentIds.map((studentId) => ({
            id: getFormIdField(studentId, sectionId, assignmentId),
            studentId,
            sectionId,
            assignmentId,
            status: currentAssignmentStudents[studentId],
          })),
        ),
      );

      // Update form with this students' info if we're in the Update Settings context.
      if (!isAssign) {
        const currentAssignmentStudentFormIds = currentAssignmentStudentIds.map(
          (studentId) => getFormIdField(studentId, sectionId, assignmentId),
        );
        const updatedStudentsFormData = formStudents.map((studentData) => {
          if (currentAssignmentStudentFormIds.includes(studentData.id)) {
            return {
              ...studentData,
              status: currentAssignmentStudents[studentData.studentId],
            };
          }

          return studentData;
        });
        dispatch(
          change(ASSIGN_MANAGE_FORM_NAME, 'students', updatedStudentsFormData),
        );
      }
    }, [assignmentStudentsStatusData]);

    useEffect(() => {
      setState({
        ...state,
        isFetchingGroupedAssignments: loadingGroupedAssignments,
      });
    }, [loadingGroupedAssignments]);

    useEffect(() => {
      const hasAssessments = assessments && assessments.length;
      const assessmentId = get(assignment, 'item.id', null);
      const assessmentsCondition =
        !hasAssessments ||
        (assessmentId &&
          !assessments.filter((assessment) => +assessment.id === +assessmentId)
            .length);
      if (!isAssign && shouldFetchOtherAssignments && assessmentsCondition) {
        setState({
          ...state,
          isFetchingGroupedAssignments: true,
        });
        fetchOtherAssignments({
          subjectId: params.subject,
          education_period: chosenEducationPeriod,
          assessment_id: assignment.item.id,
        });
      }
    }, [assignmentId, masterSubjectId, shouldFetchOtherAssignments]);

    useEffect(() => {
      if (!isLoading) {
        initForm({
          ...globals,
          ...state,
          handleShouldApplyRestriction,
          shouldApplyRestriction,
        });
      }
    }, [isLoading, assignmentId, sectionsMinTimezone, defaultTimezone]);

    useEffect(() => {
      if (formStudents.length > 0) {
        dispatch(change(ASSIGN_MANAGE_FORM_NAME, 'students', formStudents));
      }
    }, [assignmentId]);

    useEffect(() => {
      /*
       * prevProps.formData - avoids calling `updateGlobalForm` after the init
       * from AssignManageForm redux connection.
       * */
      const prevFormAssignmentId = (prevProps.formData || {}).assignmentId;
      const currentFormAssignmentId = formData.assignmentId;
      if (
        !!isLoading ||
        !prevProps.formData ||
        prevFormAssignmentId !== currentFormAssignmentId
      )
        return;

      const { printToggle: newPrintToggle } = formData;
      const { printToggle: prevPrintToggle } = prevProps.formData;

      if (!prevPrintToggle && newPrintToggle) {
        globalStoredFormValues.current = {
          startsAt,
          dueAt,
          timer,
        };
      }

      shouldApplyRestriction = handleShouldApplyRestriction();
      const allAssigneesTimezones = allAssignees.map(
        (assignee) => assignee.timezone,
      );
      const selectedSectionsMinTimezone =
        getMinTimezone(allAssigneesTimezones) || defaultTimezone;
      updateGlobalForm({
        prevProps,
        shouldApplyRestriction,
        isNotPreAPSubjectAndNotPPC,
        selectedSectionsMinTimezone,
        hasAudioRecordOrFileUpload,
      });
    }, [formData]);

    /*
     * Effect to verify if submit button should be enabled or not
     */
    useEffect(() => {
      if (isLoading || isGlobalLoading) return;

      const hasAssignees = allAssignees.length > 0 || formStudents.length > 0;
      const hasAssigneesAndPreassignedBehavior =
        hasAssignees && hasPreassignedBehavior;
      const hasDatesSelected =
        !isFinalExam ||
        (partType === PartType.FULL && !!startsAt) ||
        (partType === PartType.PARTIAL && !!startsAt && !!startsAtPart2);
      let enableSubmitBtn =
        (!isAssign || hasAssigneesAndPreassignedBehavior
          ? hasUpdatedOptions()
          : hasAssignees) && hasDatesSelected;

      /* Remove this check. We don't need it for now. More details in FY-20729 */
      // if (isAssign && showAttestationFlag && (hasSecureQns || isPracticeExam)) {
      //   enableSubmitBtn = enableSubmitBtn && attestationAgreementChecked;
      // }

      if (isFinalExam) enableSubmitBtn = enableSubmitBtn && testTakingLocation;

      setGlobalCanSubmit(enableSubmitBtn);
    }, [
      isLoading,
      allAssignees,
      attestationAgreementChecked,
      printToggle,
      scoreDisplay,
      startsAt,
      startsAtPart2,
      dueAt,
      timer,
      title,
      isSecure,
      hasScrambledOrder,
      studentScoring,
      partType,
      formStudents,
      assignmentId,
      testTakingLocation,
      allowLateSubmission,
      isSoftTimer,
    ]);

    useEffect(() => {
      if (!isAssign && selectedAssignment && assignment) {
        const selectedAssignmentId =
          selectedAssignment && selectedAssignment.id;
        const isSameAssignment =
          Number(selectedAssignmentId) === Number(assignmentId);
        setState({
          ...state,
          isLoading: !isSameAssignment,
        });
      }
    }, [assignmentId, selectedAssignment.id]);

    /**
     * when the assign request is done, this will trigger the callback from parent components
     */
    useEffect(() => {
      if (isAssign && assignmentsResponse) {
        const availableAssigneeIds = allSections.map((assignee) =>
          assignee.sectionId.toString(),
        );
        const availableSections = subjectClasses.filter((section) =>
          availableAssigneeIds.includes(section.id),
        );

        if (assignmentsResponse && assignmentsResponse.length) {
          const hasErrors = !!assignmentsResponse.find((r) => !!r.errors);
          !hasErrors &&
            onAssign &&
            onAssign(
              assignmentsResponse,
              allAssignees,
              formStudents,
              availableSections,
            );
        }
      }
    }, [assignmentsResponse]);

    const submit = async () => {
      await globalSubmit();

      if (isAssign) {
        const qbSubject = findQBSubject(teacherSubjects, subject);

        if (
          showAttestationFlag &&
          attestationAgreementChecked &&
          (hasSecureQns || isPracticeExam)
        ) {
          await savePreference({
            variables: {
              userId: me.initId,
              preference: ATTESTATION_PREFERENCE,
              value: 'true',
              context: ATTESTATION_SUBJECT,
              contextId: qbSubject.initId,
            },
          });
        }
      }
    };

    const handleSetSelectedAssignment = (selection) => {
      setSelectedAssignment(selection);
      isStudentsFormChanged &&
        handleShowClassSwitchingInfoModal &&
        handleShowClassSwitchingInfoModal(true);
      if (selection && selection.id) {
        const { params: routerParams } = router;
        const isSameAssignment =
          Number(selection.id) === Number(routerParams.assignmentId);
        const url = `/${routerParams.subject}/assignments/progress/${selection.id}`;
        setSelectedAssignmentUrl(!isSameAssignment ? url : null);
      }
    };

    const showFullUpdateName = manageMultipleClassesFlag && canUpdateAll;
    const showUpdateAllButton =
      showFullUpdateName && openAssignments.length > 1;
    const selectedAssignee =
      (selectedAssignment &&
        selectedAssignment.assignees &&
        selectedAssignment.assignees[0]) ||
      {};
    const { progress = {} } = selectedAssignment || {};
    const finishedPercentage = getFinishedPercent(progress);
    const isCompleted =
      selectedAssignment &&
      (selectedAssignment.status === 'closed' || finishedPercentage === 100);

    let footerSubmitBtnText = `Update for ${
      selectedAssignee ? selectedAssignee.name : selectedAssignment.section_name
    }`;
    footerSubmitBtnText = individualStudentAssignmentFlag
      ? 'Update This Assignment'
      : individualStudentAssignmentFlag;
    footerSubmitBtnText = showFullUpdateName ? footerSubmitBtnText : null;

    const hasSecureAssessmentOption = isNotPreAPSubjectAndNotPPC
      ? shouldTurnOnSecureAssessment(
          assessmentWithQuestionsData,
          hasAudioRecordOrFileUpload,
        )
      : true;
    let shouldApplyRestriction =
      isNotPreAPSubjectAndNotPPC &&
      checkIfNoSecureQuestions(
        assessmentWithQuestionsData,
        hasPracticeExamBehavior,
      );
    const printSelectDisabled =
      isNotPreAPSubjectAndNotPPC && showAttestationFlag
        ? // ? !hasSecureAssessmentOption && shouldApplyRestriction
          false // More details in FY-22143
        : isPPC && isFullyMCQ;
    const enableSecureBrowserOnPurpose = true; // More details in FY-20224
    const secureSelectDisabled =
      hasAudioRecordOrFileUpload ||
      (enableSecureBrowserOnPurpose
        ? false
        : showAttestationFlag &&
          hasSecureAssessmentOption &&
          isNotPreAPSubjectAndNotPPC &&
          shouldApplyRestriction);
    const subjectHaveSecureOption = !(
      isPreApSubject && assessmentWithQuestionsData.isSecure
    );
    const hiddenClass = `ft-amf-option-details visuallyhidden ${
      isFinalExam ? 'hide-final-exam' : ''
    }`;
    const studentScoringConditions = showStudentScoring;
    const withAdvancedSettings = !(isPreApSubject || isSPP);
    const advancedSettingsOpenedDefault = Boolean(
      isSecure ||
        title ||
        hasScrambledOrder ||
        studentScoring ||
        hasForcedScrambledOrder,
    );
    const studentAssignmentPath = isSPP
      ? 'assessments/spp/'
      : 'assessments/assignments/';
    const shareLinkData = isAssign
      ? null
      : {
          header: isSPP ? 'Share Resource' : 'Share Assessment',
          link: `${FYM_FRONTEND_URL}/${masterSubjectId}/${studentAssignmentPath}${assignment.id}/`,
          ctaText: 'Copy',
          timeout: 3,
        };

    const assessmentProps = {
      isMCQPPC,
      showRenameDropdown: !isPreApSubject,
      printSelectDisabled,
      secureSelectDisabled,
      showFormatOption: !(isPreApSubject || isSPP),
      showLdbOption: !printToggle && ldbEnabled && subjectHaveSecureOption,
      showScrambleQuestionsOption: !isPreApSubject,
      disableScrambleQuestionsOption: isPracticeExam,
      section:
        assignment && assignment.assignees ? assignment.assignees[0] : null,
      hasPreassignedBehavior,
      currentAssignmentHasNoSubStudents:
        individualStudentAssignmentFlag && hasNoSubStudents,
      submit,
      openAssignments,
      withAdvancedSettings,
      advancedSettingsOpenedDefault,
      hiddenClass,
      studentScoringConditions,
      setSelectedAssignment: handleSetSelectedAssignment,
      showFullUpdateName,
      showUpdateAllButton,
      selectedAssignee,
      finishedPercentage,
      isCompleted,
      footerSubmitBtnText,
      isUpdateAllEnabled,
      showTimeSettings: !printToggle || isFinalExam,
      showTimer: !(isFinalExam || isSPP),
      showStartDateResetButton: true,
      shareLinkData,
      isFullyMCQ,
      isFormatPrint: printToggle,
    };

    return (
      <WrappedComponent
        isLoading={isGlobalLoading || isLoading}
        {...omit(props, ['isLoading', 'globals'])}
        {...assessmentProps}
      />
    );
  };

  return compose(withAssessmentGroupInfo)(InnerComponent);
};

AssessmentWrapper.displayName = 'AssessmentWrapper';

export default AssessmentWrapper;
